import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { sendMessageInputSchema } from '@orbit-sync/shared-types';
import { useAuthStore } from '../auth/authStore.js';
import { useChatStore } from '../chat/chatStore.js';
import { dirFor } from '../chat/direction.js';
import { useDirectory } from '../chat/directoryStore.js';
import { memberLabel } from '../chat/labels.js';
import { useSettingsStore } from '../settings/settingsStore.js';
import { useSocket } from '../socket/SocketProvider.js';
import { EmojiPicker } from './EmojiPicker.js';
import { Icon } from './Icon.js';

/** The textarea grows to this many pixels, then scrolls. Mirrors the CSS cap. */
const MAX_TEXTAREA_PX = 168;

/** At most one "typing" signal this often; the receiver shows it for TYPING_TTL_MS. */
const TYPING_SIGNAL_MS = 3_000;

/**
 * Message input.
 *
 * Validated with the server's own `sendMessageInputSchema`, so the length limit
 * the server enforces is the one the UI enforces - no second copy to drift.
 */
export function MessageComposer({
  channelId,
  children,
}: {
  channelId: string;
  /** Slot for the attach control. */
  children?: ReactNode;
}) {
  const { socket, status } = useSocket();
  const sendMessage = useChatStore((state) => state.sendMessage);
  const replyingTo = useChatStore((state) => state.replyingTo[channelId] ?? null);
  const setReplyingTo = useChatStore((state) => state.setReplyingTo);
  const currentUserId = useAuthStore((state) => state.user?.id ?? null);
  const directory = useDirectory(replyingTo ? [replyingTo.senderId] : []);
  const lastTypingRef = useRef(0);
  // The administrator's limit; the schema's is only the hard ceiling above it.
  const maxLength = useSettingsStore((state) => state.settings.maxMessageLength);
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);

  const connected = status === 'connected' && socket !== null;

  // Auto-grow. Reset to `auto` first, or the box can only ever get taller -
  // scrollHeight of an already-tall element never reports the shorter content.
  useLayoutEffect(() => {
    const element = textareaRef.current;

    if (!element) {
      return;
    }

    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, MAX_TEXTAREA_PX)}px`;
  }, [content]);

  // Choosing "Reply" puts the caret here, ready to answer.
  useEffect(() => {
    if (replyingTo) {
      textareaRef.current?.focus();
    }
  }, [replyingTo]);

  useEffect(() => {
    if (!emojiOpen) {
      return;
    }

    function onPointerDown(event: MouseEvent): void {
      if (!emojiRef.current?.contains(event.target as Node)) {
        setEmojiOpen(false);
      }
    }

    // The DOM event, not React's synthetic one - this is a document listener.
    function onKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.key === 'Escape') {
        setEmojiOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [emojiOpen]);

  function submit(): void {
    const trimmed = content.trim();

    if (trimmed.length === 0) {
      return;
    }

    if (trimmed.length > maxLength) {
      setError(`Messages can be at most ${maxLength} characters.`);
      return;
    }

    const parsed = sendMessageInputSchema.safeParse({ channelId, content: trimmed });

    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'That message cannot be sent');
      return;
    }

    if (!socket) {
      setError('Not connected');
      return;
    }

    setError(null);
    setContent('');
    lastTypingRef.current = 0;
    sendMessage({
      socket,
      channelId,
      content: trimmed,
      replyTo: replyingTo
        ? {
            id: replyingTo.id,
            senderId: replyingTo.senderId,
            content: replyingTo.content,
            type: replyingTo.type,
          }
        : null,
    });
    setReplyingTo(channelId, null);
  }

  function handleChange(value: string): void {
    setContent(value);

    const now = Date.now();

    if (socket && value.trim().length > 0 && now - lastTypingRef.current >= TYPING_SIGNAL_MS) {
      lastTypingRef.current = now;
      socket.emit('chat.typing', { channelId });
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    submit();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    // Enter sends; Shift+Enter is a newline, which is what people expect in chat.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    } else if (event.key === 'Escape' && replyingTo) {
      setReplyingTo(channelId, null);
    }
  }

  /** Inserts at the caret rather than appending, so an emoji can go mid-sentence. */
  function insertEmoji(emoji: string): void {
    const element = textareaRef.current;
    const start = element?.selectionStart ?? content.length;
    const end = element?.selectionEnd ?? content.length;

    setContent(`${content.slice(0, start)}${emoji}${content.slice(end)}`);
    setEmojiOpen(false);

    // After the state lands, put the caret back after what was inserted.
    requestAnimationFrame(() => {
      const caret = start + emoji.length;
      element?.focus();
      element?.setSelectionRange(caret, caret);
    });
  }

  const length = content.trim().length;
  const canSend = connected && length > 0 && length <= maxLength;
  // Shown only near the limit, where it is information rather than noise.
  const showCount = length >= maxLength * 0.8;

  return (
    <form className="composer" onSubmit={handleSubmit}>
      {error ? (
        <p className="field-error">
          <Icon name="alertCircle" size={13} />
          {error}
        </p>
      ) : null}

      {replyingTo ? (
        <div className="composer-reply">
          <Icon name="reply" size={16} />
          <span className="composer-reply-body">
            <span className="composer-reply-author">
              Replying to {memberLabel(replyingTo.senderId, currentUserId, directory)}
            </span>
            <span className="composer-reply-text truncate" dir={dirFor(replyingTo.content)}>
              {replyingTo.content}
            </span>
          </span>
          <button
            type="button"
            className="btn btn--icon btn--sm"
            aria-label="Cancel reply"
            onClick={() => setReplyingTo(channelId, null)}
          >
            <Icon name="x" size={14} />
          </button>
        </div>
      ) : null}

      <div className={connected ? 'composer-shell' : 'composer-shell composer-shell--disabled'}>
        <div className="composer-tools">{children}</div>

        <textarea
          ref={textareaRef}
          rows={1}
          value={content}
          // Follows what is being typed, so Persian lays out right-to-left as it is written.
          dir={dirFor(content)}
          placeholder={connected ? 'Write a message…' : 'Waiting for the connection…'}
          aria-label="Message"
          onChange={(event) => handleChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />

        <div className="composer-tools">
          <div className="popover-anchor" ref={emojiRef}>
            <button
              type="button"
              className="btn btn--icon"
              aria-label="Insert emoji"
              aria-expanded={emojiOpen}
              onClick={() => setEmojiOpen((open) => !open)}
            >
              <Icon name="smile" size={18} />
            </button>

            {emojiOpen ? <EmojiPicker onPick={insertEmoji} /> : null}
          </div>

          <button
            type="submit"
            className="btn btn--primary btn--icon btn--round composer-send"
            disabled={!canSend}
            aria-label="Send message"
            title="Send  ·  Enter"
          >
            <Icon name="send" size={16} />
          </button>
        </div>
      </div>

      <div className="composer-foot">
        <span className="composer-hint">
          <kbd>Enter</kbd> to send · <kbd>Shift</kbd>+<kbd>Enter</kbd> for a new line
        </span>
        {connected ? null : <span>Reconnecting — messages cannot be sent right now.</span>}
        {showCount ? (
          <span
            className={
              length > maxLength ? 'composer-count composer-count--over' : 'composer-count'
            }
          >
            {length}/{maxLength}
          </span>
        ) : null}
      </div>
    </form>
  );
}
